/**
 * Canonical LLM-facing rendering of a materialised dataset summary.
 * Pure JSX consumed by the instructions reconciler, which turns it into
 * GFM markdown — the model reads tables far more reliably than free
 * prose. Everything renders from persisted FACTS at render time — the
 * facts-only contract lives in `dataset-summary.ts`'s module doc, and
 * presentation improvements apply retroactively to persisted threads.
 *
 * Architecture: `DatasetPreview` is the one exported component and the
 * single adapter that dissolves the persisted envelope into flat scope
 * DATA (`DatasetContext` — facts + provenance). Every component below
 * it is propless, reads the scope through `useDataset`, and decides its
 * own condition and rendering; props exist only for per-item identity
 * (`column`, `entry` — the map variable). Data-quality signals are
 * self-guarding with TOTAL guards: the single `<DatasetSignals>` list is
 * mounted by every shape view, and each signal derives its complete
 * firing condition — including compactness — from scope facts. Routers
 * select layout, never reachability: a mount list never encodes which
 * signals can fire.
 *
 * Only text that lands inside a single markdown table cell is
 * assembled as a string — cells flatten to one line in GFM, so that
 * boundary is the serialisation format's, not a style choice.
 *
 * Generic over any {@link DatasetSummary}; not QKG-specific.
 */

import { createContext, use } from 'react';

import { formatDisplayInstant } from '#core/lib/expr/ops/transform/date-utils.ts';
import type { AnomalyEvent } from '#core/lib/timeseries/insights/types.ts';
import { env } from '#core/lib/utils/env.ts';

import {
  type ColumnUnitFormatter,
  columnUnitFormatter,
} from '#plugin-chat/components/tools/helpers/format-with-unit.ts';
import type { ColumnStats } from '#plugin-chat/lib/tool/auto-stats.ts';
import type { DatasetColumnSummary, DatasetSummary } from '#plugin-chat/lib/tool/dataset-summary.ts';
import type { RecoveryEntry } from '#plugin-chat/lib/tool/recovery.ts';
import { useCompact } from '#plugin-chat/lib/tool/TranscriptContext.tsx';

export interface DatasetPreviewProps {
  summary: DatasetSummary;
  /** Binding name (the tool's `outputDataset`) — what later expressions reference. */
  binding: string;
  /** Materialisation state (`materialized` / `lazy`) rendered in the header. */
  state: string;
  /** Provenance label (e.g. the CDF view externalId); omitted for transforms. */
  source?: string;
}

/**
 * Null-fraction at or above which a column surfaces a mostly-null
 * signal (provided it isn't already entirely null, which gets the
 * stronger all-null signal). 50% means "more than half the rows have a
 * gap here" — enough to break naive averages or category counts if the
 * agent doesn't filter the nulls out first.
 */
const MOSTLY_NULL_THRESHOLD = 0.5;

/** The dataset facts + provenance, flattened — ambient DATA for every component in the preview. */
type DatasetScope = Omit<DatasetPreviewProps, 'summary'> &
  DatasetSummary &
  Readonly<{
    /** Whether this preview renders in the compact (previous-turn) transcript context. */
    compact: boolean;
    /** Whether any column carries insights — one derivation for the legend and the Insights table column. */
    hasInsights: boolean;
  }>;

const DatasetContext = createContext<DatasetScope | null>(null);

/** Ambient dataset scope; fail-fast outside `<DatasetPreview>`. */
function useDataset(): DatasetScope {
  const scope = use(DatasetContext);
  if (scope === null) {
    throw new Error('useDataset must render inside <DatasetPreview>');
  }
  return scope;
}

/**
 * The single adapter between the persisted envelope and the render
 * scope: the summary dissolves into flat facts here and is never
 * mentioned again below this point.
 */
export function DatasetPreview({ summary, ...provenance }: DatasetPreviewProps) {
  const compact = useCompact();
  return (
    <DatasetContext
      value={{
        ...provenance,
        ...summary,
        compact,
        hasInsights: summary.columns.some((c) => c.insights !== undefined),
      }}
    >
      <DatasetBody />
    </DatasetContext>
  );
}

/**
 * Four shapes, picked by the record's turn state and the result facts:
 *
 * - **compact** (a successful result from a *previous* agent turn) —
 *   the one-line header plus the signals; the latest-result-only ones
 *   gate themselves out on the `compact` fact.
 * - **empty** — a lead "no rows, revise and re-query" line (the binding
 *   demoted to a parenthetical), a compact column list for the retry,
 *   and any recovery context. No counts line, no profile table.
 * - **scalar** (1×1 with captured rows) — a single line carrying
 *   binding, state, column name and the value. No tables at all.
 * - **table** — legend, signals, the actual rows as a GFM table when
 *   the complete result was captured, and the per-column profile. No
 *   row sample is ever rendered: `rows` is only captured for complete
 *   results, so the model can never quote a *sample* as the dataset.
 */
function DatasetBody() {
  const { compact, rowCount, columns, rows } = useDataset();
  if (compact) {
    return <CompactDatasetView />;
  }
  if (rowCount === 0) {
    return <EmptyDatasetView />;
  }
  if (rowCount === 1 && columns.length === 1 && rows) {
    return <ScalarDatasetView />;
  }
  return <DatasetTableView />;
}

/**
 * Compact rendering for a dataset result from a *previous* agent turn:
 * the one-line header plus the signals. Which signals actually fire
 * here is each signal's own decision — the latest-result-only ones
 * (mostly-null, truncated, recovery context) gate themselves on the
 * `compact` scope fact.
 */
function CompactDatasetView() {
  return (
    <>
      <DatasetHeaderLine />
      <DatasetSignals />
    </>
  );
}

/**
 * Empty result: a retry lead, the column list, and any recovery
 * context. The signals that derive from present rows (null-fraction,
 * truncation, mixed-units) all no-op at zero rows on their own, and the
 * lead line already states the no-rows outcome.
 */
function EmptyDatasetView() {
  const { binding, columns } = useDataset();
  return (
    <>
      <p>
        Query returned no rows — revise the filter and re-query (empty dataset bound as <code>{binding}</code>).
      </p>
      {columns.length > 0 && (
        <p>
          Columns ({columns.length}): {columns.map((c) => c.name).join(', ')}.
        </p>
      )}
      <DatasetSignals />
      <RecoveryContextTable />
    </>
  );
}

/** 1×1 result with captured rows: one line, no tables. */
function ScalarDatasetView() {
  const { binding, state, source, columns, rows } = useDataset();
  const [onlyColumn] = columns;
  return (
    <>
      <p>
        <code>{binding}</code>
        {source ? ` ← ${source}` : ''} ({state}): {onlyColumn?.name} = {rows?.[0]?.[0] || 'null'}.
      </p>
      <DatasetSignals />
      <RecoveryContextTable />
    </>
  );
}

/** The full rendering: header, legend, signals, inlined rows (when captured), profile, recovery. */
function DatasetTableView() {
  const { columns, rows, hasInsights } = useDataset();
  const inline = rows !== undefined;
  // A legend carries the row-wide context once instead of repeating it
  // per cell: the display timezone (when any column renders timestamps)
  // and the enumeration hint. The hint reads the FACT of a missing
  // distribution on an enumerable column — never the summarizer's reason
  // for omitting it — and is moot when the data itself is inlined.
  const missingDistribution = inline ? [] : columns.filter((c) => (c.distinctCount ?? 0) > 0 && !c.distribution);
  const legend = [
    hasInsights ? `Times shown in ${env.displayTz}.` : null,
    missingDistribution.length > 0
      ? `Distributions omitted for: ${missingDistribution.map((c) => c.name).join(', ')} — run an \`aggregate\` to enumerate.`
      : null,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <>
      <DatasetHeaderLine />
      {legend && <p>{legend}</p>}
      <DatasetSignals />
      <InlineRowsTable />
      {columns.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Type</th>
              <th>Summary</th>
              {hasInsights && <th>Insights</th>}
            </tr>
          </thead>
          <tbody>
            {columns.map((c) => (
              <ColumnProfileRow column={c} key={c.name} />
            ))}
          </tbody>
        </table>
      )}
      <RecoveryContextTable />
    </>
  );
}

/** Shared one-line dataset header: `` `binding` ← source — N rows × M columns (state). `` */
function DatasetHeaderLine() {
  const { binding, state, source, rowCount, columns } = useDataset();
  const count = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`;
  return (
    <p>
      <code>{binding}</code>
      {source ? ` ← ${source}` : ''} — {count(rowCount, 'row')} × {count(columns.length, 'column')} ({state}).
    </p>
  );
}

/**
 * THE data-quality signal list — every signal, mounted by every shape
 * view. Each component derives its complete firing condition from the
 * scope facts (including `compact`) and renders nothing when it doesn't
 * apply, so mount position carries no information and a mounted-but-dead
 * signal cannot exist. Adding a signal is one component + one line here.
 */
function DatasetSignals() {
  return (
    <ul>
      <EmptyResultSignal />
      <AllNullSignal />
      <MostlyNullSignals />
      <TruncatedSignal />
      <InlineableProjectionSignal />
      <MixedUnitsSignals />
      <RecoveryAttachedSignal />
    </ul>
  );
}

function EmptyResultSignal() {
  const { compact, rowCount } = useDataset();
  // The full empty view states this in its lead paragraph; this bullet
  // is the compact rendering of the same fact.
  if (!compact || rowCount !== 0) {
    return null;
  }
  return <li>Query returned no rows.</li>;
}

/**
 * One line regardless of how many columns are affected — a wide result
 * with a dozen all-null columns must not spend a dozen bullets on it.
 */
function AllNullSignal() {
  const { rowCount, columns } = useDataset();
  const allNull = rowCount === 0 ? [] : columns.filter((c) => c.nullCount === rowCount).map((c) => c.name);
  const [only] = allNull;
  if (only === undefined) {
    return null;
  }
  if (allNull.length === 1) {
    return (
      <li>
        Column "{only}" is entirely null in all {rowCount} rows.
      </li>
    );
  }
  return (
    <li>
      Columns entirely null in all {rowCount} rows: {allNull.join(', ')}.
    </li>
  );
}

/** One line per affected column — a gap in one column is a distinct fact from a gap in another. */
function MostlyNullSignals() {
  const { compact, rowCount, columns } = useDataset();
  // Latest-result-only signal — too verbose for previous-turn compaction.
  if (compact || rowCount === 0) {
    return null;
  }
  return columns
    .filter((c) => c.nullCount < rowCount && c.nullCount / rowCount >= MOSTLY_NULL_THRESHOLD)
    .map((c) => (
      <li key={c.name}>
        Column "{c.name}" is null in {c.nullCount} of {rowCount} rows ({Math.round((c.nullCount / rowCount) * 100)}%
        missing) — filter out nulls before aggregating.
      </li>
    ));
}

function TruncatedSignal() {
  const { compact, rowCount, limit } = useDataset();
  // Latest-result-only signal — the raise-the-limit advice only applies
  // to the result the agent is currently acting on.
  if (compact || limit === undefined || rowCount !== limit || rowCount === 0) {
    return null;
  }
  return <li>Result truncated at limit={limit} — total may be larger; raise the limit or add filters to focus</li>;
}

function InlineableProjectionSignal() {
  const { inlineableOps } = useDataset();
  if (!inlineableOps?.length) {
    return null;
  }
  return (
    <li>
      This step only reshapes columns ({inlineableOps.join(' → ')}) and changes no rows — fold it into the next
      transform rather than binding a separate dataset.
    </li>
  );
}

/**
 * One line per unit-heterogeneous column: the boxed cells disagree, so
 * any aggregate/plot over the raw values would silently mix
 * incomparable magnitudes — surface the distribution so the agent
 * filters or converts.
 */
function MixedUnitsSignals() {
  const { columns } = useDataset();
  return columns
    .filter((c) => c.unitDistribution !== undefined)
    .map((c) => (
      <li key={c.name}>
        Column "{c.name}" mixes units: {(c.unitDistribution ?? []).map((d) => `${d.symbol} (${d.count})`).join(', ')} —
        the values are not directly comparable; filter to one unit or convert before aggregating or plotting.
      </li>
    ));
}

/**
 * Announces attached recovery context, with one clause per concurrent
 * trigger read off the same facts — an empty result, an all-null
 * column, and/or a `closeMatches` typo hint. Multiple co-occurring
 * triggers each keep their clause so the actionable hint is never
 * mis-attributed to a single reason.
 */
function RecoveryAttachedSignal() {
  const { compact, rowCount, columns, recoveryContext } = useDataset();
  // Latest-result-only signal — recovery hints are retry guidance for
  // the current turn, noise once the turn has passed.
  if (compact || !recoveryContext || recoveryContext.length === 0) {
    return null;
  }
  const allNull = rowCount > 0 && columns.some((c) => c.nullCount === rowCount);
  const typo = recoveryContext.some((r) => r.kind === 'distinct' && (r.closeMatches?.length ?? 0) > 0);
  const phrases = [
    ...(rowCount === 0 ? ['the result was empty'] : []),
    ...(allNull ? ['one or more listed columns came back fully null'] : []),
    ...(typo ? ['a filter literal looks like a typo — see the closeMatches hint'] : []),
  ].join('; ');
  return (
    <li>
      Recovery context attached for columns: {recoveryContext.map((r) => r.column).join(', ')} — these are the actual
      values / ranges present at the source; {phrases}. Revise the query against this domain.
    </li>
  );
}

/** The complete result's captured cells as a GFM data table; nothing when no rows were captured. */
function InlineRowsTable() {
  const { columns, rows } = useDataset();
  if (!rows) {
    return null;
  }
  return (
    <table>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.name}>{c.name}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rendered once through the markdown reconciler; keys are inert in the GFM output
          <tr key={i}>
            {columns.map((c, j) => (
              <td key={c.name}>{row[j] ?? ''}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const fmtStat = (n: number | null, fmt: ColumnUnitFormatter): string => (n === null ? 'n/a' : fmt.formatValue(n));

/**
 * One shared unit-formatter rung per column, fixed from its stat
 * magnitudes — the same rung labels the Type cell and renders every
 * stat magnitude, so the unit is declared once and the row stays
 * mutually comparable. Pure function of the column facts, so each cell
 * derives it independently.
 */
const columnFormatter = (column: DatasetColumnSummary): ColumnUnitFormatter =>
  columnUnitFormatter(
    column.valueType,
    column.stats?.type === 'numeric'
      ? [column.stats.min, column.stats.max, column.stats.mean].filter((n): n is number => n !== null)
      : [],
  );

/**
 * Type-aware one-line stat prose for a cell, from raw {@link ColumnStats}
 * facts. String-returning because the output lands inside a single
 * markdown table cell. Narrowing chain — adding a stats kind is a
 * compile error here until a branch is supplied.
 *
 * TODO: JSX/component wrappers instead of string interpolation
 */
const statsProse = (stats: ColumnStats, fmt: ColumnUnitFormatter): string => {
  if (stats.type === 'numeric') {
    if (stats.count === 0 || stats.mean === null) {
      return 'no numeric values';
    }
    const unit = fmt.unit ? ` ${fmt.unit}` : '';
    // A single-valued column reads as an invariant, not a range — the
    // degenerate `125–125, mean 125` form buries that fact.
    if (stats.min !== null && stats.min === stats.max) {
      return `constant ${fmtStat(stats.min, fmt)}${unit}`;
    }
    return `${fmtStat(stats.min, fmt)}–${fmtStat(stats.max, fmt)}${unit}, mean ${fmtStat(stats.mean, fmt)}`;
  }
  if (stats.type === 'date') {
    return stats.min === null || stats.max === null
      ? 'n/a'
      : `${formatDisplayInstant(stats.min) ?? 'n/a'} → ${formatDisplayInstant(stats.max) ?? 'n/a'}`;
  }
  if (stats.type === 'mixed') {
    return 'mixed types';
  }
  if (stats.type === 'string') {
    return '—';
  }
  if (stats.type === 'struct') {
    const total = stats.count + stats.nullCount;
    const members = Object.entries(stats.fields)
      .map(([name, { nonNull }]) => `${name} ${nonNull}/${total}`)
      .join(', ');
    return `{ ${members} }`;
  }
  return stats satisfies never;
};

/** One row of the column profile table. */
function ColumnProfileRow({ column }: { column: DatasetColumnSummary }) {
  const { hasInsights } = useDataset();
  const fmt = columnFormatter(column);
  return (
    <tr>
      <td>{column.name}</td>
      <td>{fmt.unit ? `${column.type} (${fmt.unit})` : column.type}</td>
      <td>
        <ColumnSummaryCell column={column} />
      </td>
      {hasInsights && (
        <td>
          <InsightsCell column={column} />
        </td>
      )}
    </tr>
  );
}

/**
 * The Summary cell: value distribution and type-aware stat prose joined
 * with ` · `, falling back to `—` when neither applies. Inlined rows
 * make the distribution redundant; a 1-row result makes stat prose
 * noise (the value IS the stat); a column without stats (string-like
 * kinds carry none — their facts are the distribution) degrades to
 * distribution only.
 *
 * Distribution prose: singleton buckets render bare (no `(1)` noise);
 * an all-unique enumeration is prefixed `N unique:` so it reads as a
 * key column, not a histogram; a single-valued column reads as the
 * invariant it is (`di-shared (all 12 rows)`). Columns without a
 * distribution fall back to the distinct count; the preamble legend
 * tells the agent to run an `aggregate` to enumerate.
 *
 * TODO: JSX/component wrappers instead of string interpolation
 */
function ColumnSummaryCell({ column }: { column: DatasetColumnSummary }) {
  const { rowCount, rows } = useDataset();
  const inline = rows !== undefined;
  const { distribution, distinctCount, stats } = column;
  const distributionProse = (): string => {
    if (!distribution) {
      return distinctCount !== undefined && distinctCount > 0 ? `${distinctCount} distinct` : '';
    }
    const [head] = distribution;
    if (distribution.length === 1 && head) {
      if (head.count === 1) {
        return head.value;
      }
      if (head.count === rowCount) {
        return `${head.value} (all ${rowCount} rows)`;
      }
      return `${head.value} (${head.count} of ${rowCount} rows)`;
    }
    if (distribution.every((d) => d.count === 1)) {
      return `${distribution.length} unique: ${distribution.map((d) => d.value).join(', ')}`;
    }
    return distribution.map((d) => (d.count === 1 ? d.value : `${d.value} (${d.count})`)).join(', ');
  };
  const prose = stats === undefined ? '' : statsProse(stats, columnFormatter(column));
  const parts = [inline ? '' : distributionProse(), rowCount === 1 || prose === '—' ? '' : prose];
  return parts.filter(Boolean).join(' · ') || '—';
}

/**
 * One numeric signal's insights as a single inline, jargon-free fact
 * line (clauses joined by `; `): trend, phases, level shifts, anomalies,
 * and seasonality; `—` when the column carries none. Timestamps are
 * formatted to ISO 8601 (epoch-ms stays the storage format).
 * Column-native magnitudes (level-shift means / magnitude, anomaly peak
 * value) carry the column's unit of measure via
 * {@link columnUnitFormatter} on a shared ladder rung fixed from this
 * column's own insight magnitudes; unit-less readings (slope %/day,
 * deviation % from normal) keep their own symbol. Descriptive stats are
 * intentionally omitted — they already appear in the Summary cell.
 * Assembled as a string because the cell flattens to a single line.
 *
 * TODO: JSX/component wrappers instead of string interpolation
 */
function InsightsCell({ column }: { column: DatasetColumnSummary }) {
  const { insights, valueType } = column;
  if (!insights) {
    return '—';
  }
  const { trend, segments, levelShifts, anomalies, seasonality, downsampledTo, pointCount } = insights;

  // One shared rung for every column-native magnitude in this column's
  // insights (level-shift means / magnitudes and anomaly peaks), so they stay
  // mutually comparable — mirrors the stat summary's shared-rung formatter.
  const magnitudes = [
    ...levelShifts.flatMap((s) => [s.beforeMean, s.afterMean, s.magnitude]),
    ...anomalies.top.map((a) => a.extremeValue),
  ];
  const { format } = columnUnitFormatter(valueType, magnitudes);

  // Timestamps render in the display timezone, dropping the time at midnight.
  const at = (ms: number): string => formatDisplayInstant(ms) ?? '';
  // Coarse rounding for unit-less magnitudes (window %, swing multiples): one
  // decimal under 10, whole numbers above — enough for the agent to quote
  // approximately ("almost 6×", "up ~5%") without false exactness.
  const coarse = (x: number): number => (Math.abs(x) >= 10 ? Math.round(x) : Math.round(x * 10) / 10);

  const windowDelta =
    trend.windowDeltaPct === null ? '' : ` (~${coarse(Math.abs(trend.windowDeltaPct))}% over the period)`;
  const phases = segments
    .map((s) => (s.transition ? `${s.transition.label} → ${s.trend.label} around ${at(s.start)}` : s.trend.label))
    .join('; ');
  const shifts = levelShifts
    .map((s) => `${s.label} around ${at(s.timestamp)} (${format(s.beforeMean)} → ${format(s.afterMean)})`)
    .join('; ');
  // An event's deviation reads as a % of the normal level when the baseline
  // supports one; only a near-zero baseline falls back to the swing multiple.
  const eventSize = (a: AnomalyEvent): string => {
    if (a.deviationPct === null) {
      return `~${coarse(a.deviationSigma)}× swing`;
    }
    return `~${coarse(Math.abs(a.deviationPct))}% ${a.type === 'spike' ? 'above' : 'below'} normal`;
  };
  const topAnomalies = anomalies.top
    .slice(0, 3)
    .map((a) => {
      const span = a.pointCount > 1 ? `${at(a.start)} → ${at(a.end)}` : `on ${at(a.start)}`;
      const peak = `${a.type === 'spike' ? 'peak' : 'low'} ${format(a.extremeValue)}`;
      const tail = a.ongoing ? ', ongoing at window end' : '';
      return `${a.label} ${span} (${peak}, ${eventSize(a)}${tail})`;
    })
    .join('; ');

  const facts: { when: boolean; text: string }[] = [
    { when: trend.significant, text: `Trend: ${trend.label}${windowDelta}` },
    { when: segments.length > 1, text: `Phases (${segments.length}): ${phases}` },
    { when: levelShifts.length > 0, text: `Level shifts: ${shifts}` },
    { when: anomalies.count > 0, text: `Anomalies (${anomalies.count}): ${topAnomalies}` },
    { when: !!seasonality.label, text: `Seasonality: ${seasonality.label}` },
    {
      when: downsampledTo !== null,
      text: `Computed from a downsampled view (${downsampledTo} of ${pointCount} points)`,
    },
  ];

  // A flat, featureless series still has insights computed — fall back to a
  // terse "steady" rather than an empty cell so the agent sees it was checked.
  const line = facts
    .filter((fact) => fact.when)
    .map((fact) => fact.text)
    .join('; ');
  return line || 'steady';
}

/**
 * A {@link RecoveryEntry}'s domain payload. `distinct`-kind entries
 * become a `v1, v2, … (N distinct)` line and, when the failing filter
 * literal looked like a typo, a "did you mean" suffix listing the
 * fuse.js Bitap-closest source value(s) — turning "filter excluded
 * everything" into "filter says X but source has Y" in one cell.
 * `range`-kind entries become `min → max`.
 */
function RecoveryDomainCell({ entry }: { entry: RecoveryEntry }) {
  if (entry.kind === 'distinct') {
    const head = entry.values.join(', ');
    const tail = entry.distinctCount > entry.values.length ? ` … (${entry.distinctCount} distinct total)` : '';
    const matches = entry.closeMatches ?? [];
    const fuzzy =
      matches.length === 0
        ? ''
        : ` — did you mean ${matches.map((m) => `"${m.suggestion}" (typed "${m.typed}")`).join(', ')}?`;
    return `${head}${tail}${fuzzy}`;
  }
  return `${entry.min ?? 'n/a'} → ${entry.max ?? 'n/a'}`;
}

/** Recovery-context table (actual source values / ranges); nothing when absent. */
function RecoveryContextTable() {
  const { recoveryContext } = useDataset();
  if (!recoveryContext || recoveryContext.length === 0) {
    return null;
  }
  return (
    <>
      <p>
        <strong>Recovery context</strong> — the actual values / ranges present at the source for the listed columns. Use
        this to revise the query against the real domain instead of guessing.
      </p>
      <table>
        <thead>
          <tr>
            <th>Column</th>
            <th>Kind</th>
            <th>Domain</th>
          </tr>
        </thead>
        <tbody>
          {recoveryContext.map((r) => (
            <tr key={r.column}>
              <td>{r.column}</td>
              <td>{r.kind}</td>
              <td>
                <RecoveryDomainCell entry={r} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
